import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, resolveComponent as _resolveComponent, vShow as _vShow, withDirectives as _withDirectives, toHandlers as _toHandlers, mergeProps as _mergeProps, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "mb-8"
}
const _hoisted_2 = ["to"]
const _hoisted_3 = {
  key: 0,
  class: "w-8 h-8 mr-2"
}
const _hoisted_4 = { class: "aspect-w-8 aspect-h-8" }
const _hoisted_5 = ["src"]
const _hoisted_6 = { class: "flex-1 min-w-0" }
const _hoisted_7 = { class: "text-left transition-colors duration-150 text-gray-700 hover:text-blue-600" }
const _hoisted_8 = { class: "text-md font-medium truncate" }
const _hoisted_9 = { class: "text-sm text-gray-700" }
const _hoisted_10 = { class: "flex flex-wrap items-center justify-start mt-2" }
const _hoisted_11 = ["href"]
const _hoisted_12 = { class: "text-sm font-medium max-w-xs truncate" }
const _hoisted_13 = {
  key: 0,
  class: "text-sm text-gray-500 max-w-sm truncate"
}
const _hoisted_14 = {
  key: 1,
  class: "text-sm"
}
const _hoisted_15 = { class: "text-sm font-medium max-w-xs truncate" }
const _hoisted_16 = {
  key: 0,
  class: "text-sm"
}
const _hoisted_17 = { class: "flex justify-between items-center gap-2" }
const _hoisted_18 = { class: "flex justify-between" }
const _hoisted_19 = { class: "text-sm font-medium" }
const _hoisted_20 = {
  key: 0,
  class: "col-span-6 gap-2"
}

import { DatePicker } from 'v-calendar'
import { computed, ref, watch } from 'vue'

import FranchiseLockNotice from '@/components/franchise/FranchiseLockNotice.vue'
import FormElement from '@/components/neue/FormElement'
import FormSection from '@/components/neue/FormSection'
import InputField from '@/components/neue/InputField'
import NeueButton from '@/components/neue/NeueButton'
import NeueToggle from '@/components/neue/NeueToggle'
import { dateObjectToServerDay, serverDayToDateObject } from '@/helpers/dates'
import { nextFranchiseManaged } from '@/helpers/franchiseManaged'
import { pairMembershipPriceRows } from '@/helpers/membershipPrices'
import api from '@/store/api'
import { mapRelationship, mapRelationships } from '@/store/mappers'


export default {
  __name: 'CPSPModal',
  props: ['id', 'open', 'location'],
  emits: ['close-modal'],
  setup(__props, { emit: __emit }) {

const props = __props
const emit = __emit

const local = ref({})

const formatDay = day =>
  new Intl.DateTimeFormat([], { dateStyle: 'full' }).format(serverDayToDateObject(day.attributes.date))

const pair = computed(() => mapRelationship({ type: 'category_product_store_pair', id: props.id }))
const store = computed(() => mapRelationship(pair.value?.relationships.store.data))
const product = computed(() => mapRelationship(pair.value?.relationships.product.data))
const image = computed(() => mapRelationships(product.value.relationships.attachments.data)[0])
const labels = computed(() => mapRelationships(product.value.relationships.labels.data))
const fallback = computed(() => mapRelationship(product.value.relationships.replace_with_product.data))
const reverseFallback = computed(() => mapRelationships(product.value.relationships.replaces_products.data))

const availabilityDays = computed(() => mapRelationships(pair.value?.relationships.fulfillment_availabilities.data))

// Everything in this modal is the franchise's on a managed pair. A response that stores the pair
// without franchise_managed must not unlock it; a different pair starts unlocked.
const rememberedLock = ref(null)
watch(
  () => props.id,
  () => (rememberedLock.value = null)
)
watch(pair, current => (rememberedLock.value = nextFranchiseManaged(rememberedLock.value, current)), {
  immediate: true
})
const managed = computed(() => Boolean(rememberedLock.value))
const franchiseName = computed(() => rememberedLock.value?.franchiseName ?? null)

const addDay = async () => {
  if (managed.value) return
  await api.call(
    'PUT',
    `stores/${store.value.id}/category_product_store_pairs/${props.id}?included=fulfillment_availabilities`,
    {
      category_product_store_pair: {
        fulfillment_availabilities_attributes: [{ date: dateObjectToServerDay(local.value.newDay) }]
      }
    }
  )
}

const deleteDay = async day => {
  if (managed.value) return
  if (!confirm(`Delete ${formatDay(day)}?`)) return
  await api.call('PUT', `stores/${store.value.id}/category_product_store_pairs/${props.id}`, {
    category_product_store_pair: { fulfillment_availabilities_attributes: [{ id: day.id, _destroy: true }] }
  })
}

watch(
  () => props.open,
  () => {
    if (props.open) {
      editPair(pair.value)
    }
  }
)

const editPair = pair => {
  local.value = {
    open: true,

    taxable: pair.attributes.taxable,
    allowsAutofill: pair.attributes.allows_autofill,
    showPrice: pair.attributes.show_prices,
    productCardStyle: pair.attributes.product_card_style,
    quantityDisplay: pair.attributes.quantity_selector_type,

    alwaysAvailable: pair.attributes.available_for_all_fulfillment_availabilities,
    newDay: new Date(),
    hasInventoryLimit: Object.fromEntries(availabilityDays.value.map(x => [x.id, x.attributes.limit_quantity])),
    inventoryLimit: Object.fromEntries(availabilityDays.value.map(x => [x.id, x.attributes.quantity_limit])),

    membershipPrices: pairMembershipPriceRows(pair)
  }
}

const savePairModal = () => {
  if (managed.value) return
  api.call(
    'PUT',
    `stores/${store.value.id}/category_product_store_pairs/${props.id}?included=fulfillment_availabilities,product_store_membership_prices`,
    {
      category_product_store_pair: {
        taxable: local.value.taxable,
        allows_autofill: local.value.allowsAutofill,
        show_prices: local.value.showPrice,
        product_card_style: local.value.productCardStyle,
        quantity_selector_type: local.value.quantityDisplay,
        available_for_all_fulfillment_availabilities: local.value.alwaysAvailable,
        product_store_membership_prices_attributes: local.value.membershipPrices.map(x => ({
          id: x.priceId,
          membership_tier_id: x.tierId,
          price_cents: Math.round(x.price * 100),
          subject_to_pricing_tiers: x.subject,
          available_for_membership_tier: x.available,
          use_default_price: x.default
        })),
        fulfillment_availabilities_attributes: availabilityDays.value.map(x => ({
          id: x.id,
          quantity_limit: local.value.inventoryLimit[x.id],
          limit_quantity: local.value.hasInventoryLimit[x.id]
        }))
      }
    }
  )
}

return (_ctx, _cache) => {
  const _component_SelectInput = _resolveComponent("SelectInput")
  const _component_router_link = _resolveComponent("router-link")
  const _component_TheModal = _resolveComponent("TheModal")

  return (_openBlock(), _createBlock(_component_TheModal, {
    "is-open": Boolean(props.open),
    "modal-title": "Edit product in store",
    onCloseModal: _cache[8] || (_cache[8] = $event => (emit('close-modal')))
  }, {
    main: _withCtx(() => [
      (managed.value)
        ? (_openBlock(), _createBlock(FranchiseLockNotice, {
            key: 0,
            "franchise-name": franchiseName.value
          }, null, 8, ["franchise-name"]))
        : _createCommentVNode("", true),
      (props.location === 'store')
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("a", {
              to: `/store/products/${product.value.id}`,
              class: "flex items-center",
              target: "_blank"
            }, [
              (local.value.image)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createElementVNode("div", _hoisted_4, [
                      _createElementVNode("img", {
                        class: "w-full h-full object-center object-cover rounded select-none",
                        src: image.value?.attributes.secure_url
                      }, null, 8, _hoisted_5)
                    ])
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("p", _hoisted_8, _toDisplayString(product.value.attributes.product_name), 1),
                  _createElementVNode("p", _hoisted_9, _toDisplayString(product.value.attributes.description), 1)
                ])
              ])
            ], 8, _hoisted_2),
            _createElementVNode("div", _hoisted_10, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(labels.value, (label) => {
                return (_openBlock(), _createElementBlock("span", {
                  key: label.id,
                  class: "mr-1 mb-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                }, _toDisplayString(label.attributes.label_name), 1))
              }), 128))
            ])
          ]))
        : _createCommentVNode("", true),
      _createVNode(_unref(FormSection), { name: "Settings" }, {
        default: _withCtx(() => [
          _createVNode(_unref(FormElement), null, {
            default: _withCtx(() => [
              _createVNode(_unref(NeueToggle), {
                modelValue: local.value.taxable,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((local.value.taxable) = $event)),
                name: "Taxable",
                disabled: managed.value,
                onChange: savePairModal
              }, null, 8, ["modelValue", "disabled"])
            ]),
            _: 1
          }),
          _createVNode(_unref(FormElement), null, {
            default: _withCtx(() => [
              _createVNode(_unref(NeueToggle), {
                modelValue: local.value.allowsAutofill,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((local.value.allowsAutofill) = $event)),
                name: "Include item in logic when autofilling orders",
                disabled: managed.value,
                onChange: savePairModal
              }, null, 8, ["modelValue", "disabled"])
            ]),
            _: 1
          }),
          _createVNode(_unref(FormElement), null, {
            default: _withCtx(() => [
              _createVNode(_unref(NeueToggle), {
                modelValue: local.value.showPrice,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => ((local.value.showPrice) = $event)),
                name: "Show price",
                disabled: managed.value,
                onChange: savePairModal
              }, null, 8, ["modelValue", "disabled"])
            ]),
            _: 1
          }),
          _createVNode(_unref(FormElement), null, {
            default: _withCtx(() => [
              _createVNode(_component_SelectInput, {
                modelValue: local.value.productCardStyle,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = $event => ((local.value.productCardStyle) = $event)),
                name: "Product card style",
                disabled: managed.value,
                options: [
              { name: 'Image upper right corner', value: 'default' },
              { name: 'Full-bleed image', value: 'enlarged-image' }
            ],
                onChange: savePairModal
              }, null, 8, ["modelValue", "disabled"])
            ]),
            _: 1
          }),
          _createVNode(_unref(FormElement), null, {
            default: _withCtx(() => [
              _createVNode(_component_SelectInput, {
                modelValue: local.value.quantityDisplay,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = $event => ((local.value.quantityDisplay) = $event)),
                name: "Quantity display type",
                disabled: managed.value,
                options: [
              { name: 'Default', value: 'default' },
              { name: 'Checkbox', value: 'checkbox' }
            ],
                onChange: savePairModal
              }, null, 8, ["modelValue", "disabled"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_unref(FormSection), { name: "Autofill replacement" }, {
        default: _withCtx(() => [
          _createVNode(_unref(FormElement), null, {
            default: _withCtx(() => [
              _cache[9] || (_cache[9] = _createElementVNode("p", { class: "text-sm" }, "Fallback product if this product is unavailable during autofill:", -1)),
              (fallback.value)
                ? (_openBlock(), _createElementBlock("a", {
                    key: 0,
                    target: "_blank",
                    class: "text-gray-900 hover:text-blue-600 transition-colors duration-150 text-left p-2 block",
                    href: '/store/products/' + fallback.value.id
                  }, [
                    _createElementVNode("p", _hoisted_12, _toDisplayString(fallback.value.attributes.product_name), 1),
                    (fallback.value.attributes.description)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(fallback.value.attributes.description), 1))
                      : _createCommentVNode("", true)
                  ], 8, _hoisted_11))
                : (_openBlock(), _createElementBlock("span", _hoisted_14, "None"))
            ]),
            _: 1
          }),
          _createVNode(_unref(FormElement), null, {
            default: _withCtx(() => [
              _cache[10] || (_cache[10] = _createElementVNode("p", { class: "text-sm" }, "Will replace these unavailable products during autofill:", -1)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(reverseFallback.value, (product) => {
                return (_openBlock(), _createBlock(_component_router_link, {
                  key: product.id,
                  class: "text-gray-900 hover:text-blue-600 transition-colors duration-150 text-left p-2 block",
                  to: '/store/products/' + product.id
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("p", _hoisted_15, _toDisplayString(product.attributes.product_name), 1),
                    _withDirectives(_createElementVNode("p", { class: "text-sm text-gray-500 max-w-sm truncate" }, _toDisplayString(product.attributes.description), 513), [
                      [_vShow, product.attributes.description]
                    ])
                  ]),
                  _: 2
                }, 1032, ["to"]))
              }), 128)),
              (!reverseFallback.value.length)
                ? (_openBlock(), _createElementBlock("span", _hoisted_16, "None"))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_unref(FormSection), { name: "Production day availability" }, {
        default: _withCtx(() => [
          _createVNode(_unref(FormElement), null, {
            default: _withCtx(() => [
              _createVNode(_unref(NeueToggle), {
                modelValue: local.value.alwaysAvailable,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = $event => ((local.value.alwaysAvailable) = $event)),
                name: "Always available",
                disabled: managed.value,
                onChange: savePairModal
              }, null, 8, ["modelValue", "disabled"])
            ]),
            _: 1
          }),
          (!local.value.alwaysAvailable && !managed.value)
            ? (_openBlock(), _createBlock(_unref(FormElement), {
                key: 0,
                "data-test": "day-picker"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_17, [
                    _createVNode(_unref(DatePicker), {
                      modelValue: local.value.newDay,
                      "onUpdate:modelValue": _cache[6] || (_cache[6] = $event => ((local.value.newDay) = $event)),
                      "available-dates": { start: new Date(), end: null },
                      class: "w-full",
                      popover: { visibility: 'click' }
                    }, {
                      default: _withCtx(({ inputValue, inputEvents }) => [
                        _createVNode(_unref(InputField), _mergeProps({ "model-value": inputValue }, _toHandlers(inputEvents)), null, 16, ["model-value"])
                      ]),
                      _: 1
                    }, 8, ["modelValue", "available-dates"]),
                    _createVNode(_unref(NeueButton), {
                      type: "secondary",
                      onClick: addDay
                    }, {
                      default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                        _createTextVNode("Add", -1)
                      ]))]),
                      _: 1
                    })
                  ])
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availabilityDays.value, (day) => {
            return (_openBlock(), _createBlock(_unref(FormElement), { class: "text-gray-900 text-left my-2" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_18, [
                  _createElementVNode("p", _hoisted_19, _toDisplayString(formatDay(day)), 1),
                  (!managed.value)
                    ? (_openBlock(), _createBlock(_unref(NeueButton), {
                        key: 0,
                        type: "secondary",
                        onClick: $event => (deleteDay(day))
                      }, {
                        default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                          _createTextVNode("Delete", -1)
                        ]))]),
                        _: 1
                      }, 8, ["onClick"]))
                    : _createCommentVNode("", true)
                ]),
                _createVNode(_unref(NeueToggle), {
                  modelValue: local.value.hasInventoryLimit[day.id],
                  "onUpdate:modelValue": $event => ((local.value.hasInventoryLimit[day.id]) = $event),
                  name: "Has inventory limit",
                  disabled: managed.value,
                  onChange: savePairModal
                }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"]),
                (local.value.hasInventoryLimit[day.id])
                  ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                      _createVNode(_unref(InputField), {
                        modelValue: local.value.inventoryLimit[day.id],
                        "onUpdate:modelValue": $event => ((local.value.inventoryLimit[day.id]) = $event),
                        name: "Inventory limit",
                        prefix: "#",
                        disabled: managed.value,
                        onChange: savePairModal
                      }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"])
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _: 2
            }, 1024))
          }), 256))
        ]),
        _: 1
      }),
      _createVNode(_unref(FormSection), { name: "Membership pricing" }, {
        default: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(local.value.membershipPrices, (price) => {
            return (_openBlock(), _createBlock(_unref(FormElement), { class: "flex flex-col gap-3" }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(price.name) + " ", 1),
                _createVNode(_unref(NeueToggle), {
                  modelValue: price.subject,
                  "onUpdate:modelValue": $event => ((price.subject) = $event),
                  name: "Subject to pricing tiers",
                  disabled: managed.value,
                  onChange: savePairModal
                }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"]),
                _createVNode(_unref(NeueToggle), {
                  modelValue: price.available,
                  "onUpdate:modelValue": $event => ((price.available) = $event),
                  name: "Available for membership tier",
                  disabled: managed.value,
                  onChange: savePairModal
                }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"]),
                _createVNode(_unref(NeueToggle), {
                  modelValue: price.default,
                  "onUpdate:modelValue": $event => ((price.default) = $event),
                  name: "Use default price",
                  disabled: managed.value,
                  onChange: savePairModal
                }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"]),
                (!price.default)
                  ? (_openBlock(), _createBlock(_unref(InputField), {
                      key: 0,
                      modelValue: price.price,
                      "onUpdate:modelValue": $event => ((price.price) = $event),
                      name: "Dollar amount",
                      prefix: "$",
                      disabled: managed.value,
                      onChange: savePairModal
                    }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"]))
                  : _createCommentVNode("", true)
              ]),
              _: 2
            }, 1024))
          }), 256))
        ]),
        _: 1
      })
    ]),
    footer: _withCtx(() => [
      _createVNode(_unref(NeueButton), {
        type: "primary",
        onClick: _cache[7] || (_cache[7] = $event => (emit('close-modal')))
      }, {
        default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
          _createTextVNode("Done", -1)
        ]))]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["is-open"]))
}
}

}